/* SBJ Custom Studio. Progressive enhancement; Shopify owns the actual form POST. */
(() => {
  'use strict';
  if (!window.customElements || customElements.get('sbj-custom-studio')) return;

  const $ = (root, selector) => root.querySelector(selector);
  const $$ = (root, selector) => Array.from(root.querySelectorAll(selector));
  const motionQuery = () => window.matchMedia('(prefers-reduced-motion: reduce)');

  class StudioForm {
    constructor(root, signal) {
      this.root = root;
      this.signal = signal;
      this.form = $(root, '.sbjcs-form');
      this.card = $(root, '[data-form-card]');
      this.panels = $$(root, '[data-step]');
      this.motion = motionQuery();
      this.index = 0;
      this.transitioning = false;
      this.animations = [];
      this.storageKey = `sbj-custom-request:${root.id}:${location.pathname}`;
      this.success = $(root, '[data-success]');
      if (this.success) {
        this.clearSavedSubmission();
        requestAnimationFrame(() => { if (!signal.aborted) this.success.focus(); });
        return;
      }
      if (!this.form || this.panels.length !== 3) return;
      this.restoreSubmission();
      this.submitButton = $(root, '[data-submit]');
      this.submitText = $(root, '[data-submit-text]');
      this.initialSubmitText = this.submitText.textContent;
      this.shell = $(root, '[data-step-shell]');
      this.backButton = $(root, '[data-back]');
      this.continueButton = $(root, '[data-continue]');

      this.continueButton.addEventListener('click', () => this.advance(), { signal });
      this.backButton.addEventListener('click', () => this.go(this.index - 1), { signal });
      $(root, '[data-edit-piece]').addEventListener('click', () => this.go(0), { signal });
      this.form.addEventListener('input', (event) => this.clearFieldIfValid(event), { signal });
      this.form.addEventListener('change', (event) => this.clearFieldIfValid(event), { signal });
      this.form.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' && !event.isComposing && event.target.tagName === 'INPUT' && this.index < 2) {
          event.preventDefault();
          this.advance();
        }
      }, { signal });
      this.form.addEventListener('submit', (event) => this.onSubmit(event), { signal });
      window.addEventListener('pageshow', () => this.resetBusy(), { signal });
      this.motion.addEventListener('change', () => {
        if (this.motion.matches) this.animations.forEach((animation) => { try { animation.finish(); } catch (_) { /* Already cancelled. */ } });
      }, { signal });

      // Only hide panels after listeners and native fallback are safely in place.
      this.form.noValidate = true;
      this.form.classList.add('is-enhanced');
      $(root, '[data-progress]').hidden = false;
      this.setStep(0);
      if ($(root, '[data-server-error]')) {
        // Show the contact step after a server rejection; all steps remain editable.
        this.setStep(2);
        requestAnimationFrame(() => {
          if (!signal.aborted) $(root, '[data-server-error]').focus();
        });
      }
    }
    fields(panel) { return $$(panel, '[data-field]'); }
    checkField(field, show = true) {
      const inputs = $$(field, 'input, select, textarea').filter((input) => !input.disabled);
      const radios = inputs.filter((input) => input.type === 'radio');
      let badInput = null;
      if (radios.length) {
        if ((field.dataset.required === 'true' || radios.some((radio) => radio.required)) && !radios.some((radio) => radio.checked)) badInput = radios[0];
      } else {
        badInput = inputs.find((input) => (input.required && !input.value.trim()) || !input.validity.valid) || null;
        const urlInput = inputs.find((input) => input.type === 'url' && input.value.trim());
        if (urlInput && !badInput) {
          try {
            const parsed = new URL(urlInput.value.trim());
            if (!['https:', 'http:'].includes(parsed.protocol)) badInput = urlInput;
          } catch (_) { badInput = urlInput; }
        }
      }
      if (show) {
        inputs.forEach((input) => {
          if (badInput) input.setAttribute('aria-invalid', 'true');
          else input.removeAttribute('aria-invalid');
        });
        const error = $(field, '[data-error]');
        if (error) {
          error.hidden = !badInput;
          error.textContent = badInput ? (field.dataset.errorMessage || 'Please check this field.') : '';
        }
      }
      return badInput;
    }
    validate(index, focus = true) {
      const errors = this.fields(this.panels[index]).map((field) => this.checkField(field)).filter(Boolean);
      if (errors.length && focus) {
        errors[0].focus();
        $(this.root, '[data-form-status]').textContent = `Please check ${errors.length === 1 ? 'the highlighted field' : 'the ' + errors.length + ' highlighted fields'}.`;
      }
      return errors.length === 0;
    }
    clearFieldIfValid(event) {
      const field = event.target.closest('[data-field]');
      if (field && $(field, '[aria-invalid="true"]') && !this.checkField(field, false)) this.checkField(field);
      if (this.index === 2) this.updateReview();
    }
    advance() {
      if (this.transitioning || !this.validate(this.index)) return;
      this.go(Math.min(this.index + 1, 2));
    }
    setStep(index) {
      this.index = index;
      this.panels.forEach((panel, panelIndex) => { panel.hidden = panelIndex !== index; });
      this.backButton.hidden = index === 0;
      this.continueButton.hidden = index === 2;
      this.submitButton.hidden = index !== 2;
      $(this.root, '[data-step-count]').textContent = `Step ${index + 1} of 3`;
      const progress = $(this.root, '[data-progressbar]');
      progress.setAttribute('aria-valuenow', String(index + 1));
      progress.setAttribute('aria-valuetext', `Step ${index + 1} of 3: ${this.panels[index].dataset.stepName}`);
      $(this.root, '[data-progress-fill]').style.width = `${(index + 1) / 3 * 100}%`;
      $$(this.root, '[data-step-label]').forEach((label, labelIndex) => {
        if (labelIndex === index) label.setAttribute('aria-current', 'step');
        else label.removeAttribute('aria-current');
      });
      $(this.root, '[data-form-status]').textContent = '';
      if (index === 2) this.updateReview();
    }
    async go(index, focus = true) {
      if (index < 0 || index >= this.panels.length || index === this.index || this.transitioning || this.signal.aborted) return;
      const direction = index > this.index ? 1 : -1;
      const previous = this.panels[this.index];
      const next = this.panels[index];
      const animate = !this.motion.matches && typeof next.animate === 'function';
      this.transitioning = true;
      const oldHeight = this.shell.getBoundingClientRect().height;
      try {
        if (animate) {
          const exit = previous.animate([{ opacity: 1, transform: 'translateX(0)' }, { opacity: 0, transform: `translateX(${-6 * direction}px)` }], { duration: 90, easing: 'ease-out' });
          this.animations = [exit];
          await exit.finished;
        }
        if (this.signal.aborted) return;
        this.setStep(index);
        if (focus) this.focusStep(next);
        if (animate) {
          const newHeight = this.shell.getBoundingClientRect().height;
          this.shell.style.overflow = 'hidden';
          const resize = this.shell.animate([{ height: `${oldHeight}px` }, { height: `${newHeight}px` }], { duration: 240, easing: 'cubic-bezier(.2,.7,.2,1)' });
          const enter = next.animate([{ opacity: 0, transform: `translateX(${10 * direction}px)` }, { opacity: 1, transform: 'translateX(0)' }], { duration: 220, easing: 'cubic-bezier(.2,.7,.2,1)' });
          this.animations = [resize, enter];
          await Promise.all(this.animations.map((animation) => animation.finished));
        }
      } catch (_) {
        // Editor re-renders/navigation may cancel animations; never strand the form.
      } finally {
        this.shell.style.overflow = '';
        this.transitioning = false;
        this.animations = [];
      }
    }
    focusStep(panel) {
      const heading = $(panel, '[data-step-heading]');
      heading.focus({ preventScroll: true });
      const rectangle = heading.getBoundingClientRect();
      const topOffset = parseFloat(getComputedStyle(this.root).getPropertyValue('--sbj-sticky-top')) || 96;
      if (rectangle.top < topOffset || rectangle.bottom > window.innerHeight - 80) {
        this.card.scrollIntoView({ block: 'start', behavior: this.motion.matches ? 'auto' : 'smooth' });
      }
    }
    updateReview() {
      const data = new FormData(this.form);
      const piece = data.get('contact[Piece type]');
      const metal = data.get('contact[Preferred metal]');
      const diamond = data.get('contact[Preferred diamond]');
      const rawBudget = data.get('contact[Budget USD]');
      const entries = [piece, metal, diamond].filter(Boolean);
      if (rawBudget !== null && rawBudget !== '' && Number.isFinite(Number(rawBudget))) {
        entries.push(new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(Number(rawBudget)) + ' budget');
      }
      $(this.root, '[data-review]').hidden = !entries.length;
      $(this.root, '[data-review-text]').textContent = entries.join(' · ');
    }
    async onSubmit(event) {
      if (this.transitioning) { event.preventDefault(); return; }
      if (this.index !== 2) { event.preventDefault(); this.advance(); return; }
      // Email validation alone is not enough: revisit any invalid earlier panel.
      let invalidIndex = -1;
      for (let index = 0; index < this.panels.length; index += 1) {
        if (!this.validate(index, false) && invalidIndex === -1) invalidIndex = index;
      }
      if (invalidIndex !== -1) {
        event.preventDefault();
        if (invalidIndex !== this.index) await this.go(invalidIndex, false);
        this.validate(invalidIndex);
        return;
      }
      this.saveSubmission();
      // DO NOT fetch(), call form.submit(), or preventDefault() here.
      // Shopify's native form and hCaptcha must be allowed to complete normally.
      this.form.setAttribute('aria-busy', 'true');
      this.submitText.textContent = 'Sending…';
      clearTimeout(this.busyTimer);
      this.busyTimer = setTimeout(() => this.resetBusy(), 12000);
    }
    resetBusy() {
      if (!this.form || !this.submitText) return;
      this.form.removeAttribute('aria-busy');
      this.submitText.textContent = this.initialSubmitText;
    }
    saveSubmission() {
      // Only on submission, only for this tab, expires after 15 minutes.
      // Recovers input after CAPTCHA / a server rejection; not an analytics draft.
      try {
        const values = {};
        new FormData(this.form).forEach((value, name) => {
          if (name.startsWith('contact[') && typeof value === 'string') values[name] = value;
        });
        sessionStorage.setItem(this.storageKey, JSON.stringify({ expires: Date.now() + 15 * 60 * 1000, values }));
      } catch (_) { /* The form still works with storage disabled. */ }
    }
    restoreSubmission() {
      try {
        const raw = sessionStorage.getItem(this.storageKey);
        if (!raw) return;
        const saved = JSON.parse(raw);
        if (!saved || !saved.values || !Number.isFinite(saved.expires) || saved.expires < Date.now()) {
          this.clearSavedSubmission();
          return;
        }
        $$(this.form, 'input, textarea, select').forEach((input) => {
          if (input.type === 'hidden' || !Object.prototype.hasOwnProperty.call(saved.values, input.name)) return;
          const value = saved.values[input.name];
          if (typeof value !== 'string') return;
          if (input.type === 'radio') input.checked = input.value === value;
          else input.value = value;
        });
      } catch (_) { this.clearSavedSubmission(); }
    }
    clearSavedSubmission() { try { sessionStorage.removeItem(this.storageKey); } catch (_) { /* Storage optional. */ } }
    destroy() { clearTimeout(this.busyTimer); this.animations.forEach((animation) => animation.cancel()); }
  }

  class SBJCustomStudio extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      // Allows child markup to arrive when Shopify inserts an editor-rendered section.
      queueMicrotask(() => {
        if (signal.aborted || !this.isConnected) return;
        // Native dialog provides keyboard focus trapping and Escape-to-close behavior.
        this.dialog = $(this, '[data-inquiry-dialog]');
        const openInquiry = (event) => {
          if (!this.dialog || typeof this.dialog.showModal !== 'function') return;
          if (event) event.preventDefault();
          this.returnFocus = event ? event.currentTarget : null;
          if (!this.dialog.open) {
            this.previousOverflow = document.body.style.overflow;
            this.dialog.showModal();
            document.body.style.overflow = 'hidden';
          }
          const heading = $(this.dialog, '[data-success], [data-server-error], [data-step]:not([hidden]) [data-step-heading]');
          if (heading) heading.focus({ preventScroll: true });
        };
        $$(this, '[data-open-inquiry]').forEach((button) => button.addEventListener('click', openInquiry, { signal }));
        if (this.dialog) {
          $(this, '[data-close-inquiry]').addEventListener('click', () => this.dialog.close(), { signal });
          this.dialog.addEventListener('close', () => {
            document.body.style.overflow = this.previousOverflow || '';
            if (this.returnFocus && this.returnFocus.isConnected) this.returnFocus.focus({ preventScroll: true });
          }, { signal });
          // Reopen the result after Shopify's native POST redirects back to this page.
          if ($(this, '[data-success], [data-server-error]') || location.hash === '#' + this.id.replace('SBJStudio-', 'SBJRequest-')) openInquiry();
        }
        // Stop portfolio videos when another starts or the visitor leaves the tab.
        const videos = $$(this, '.sbjpf-portfolio video');
        videos.forEach((video) => video.addEventListener('play', () => videos.forEach((other) => { if (other !== video) other.pause(); }), { signal }));
        document.addEventListener('visibilitychange', () => { if (document.hidden) videos.forEach((video) => video.pause()); }, { signal });
        try { this.formController = new StudioForm(this, signal); }
        catch (error) {
          const form = $(this, '.sbjcs-form');
          if (form) { form.noValidate = false; form.classList.remove('is-enhanced'); }
          $$(this, '[data-step]').forEach((step) => { step.hidden = false; });
          $$(this, '[data-progress], [data-back], [data-continue]').forEach((control) => { control.hidden = true; });
          const submit = $(this, '[data-submit]');
          if (submit) submit.hidden = false;
          console.warn('SBJ step enhancement unavailable; the full native form remains available.', error);
        }
      });
    }
    disconnectedCallback() {
      if (this.controller) this.controller.abort();
      if (this.dialog && this.dialog.open) { this.dialog.close(); document.body.style.overflow = this.previousOverflow || ''; }
      if (this.formController) this.formController.destroy();
      this.controller = null;
      this.dialog = null;
      this.formController = null;
    }
  }
  customElements.define('sbj-custom-studio', SBJCustomStudio);
})();
